import {
  createController,
  currentMonthFirst,
  emptyOptionSearch,
  optionQueryAction,
  parseMonth,
} from '@eo/shared';

import {
  createSupplement,
  fetchClientCapacity,
  removeAssignment,
  removeSupplement,
  searchMembers,
  upsertAssignment,
  upsertBudget,
} from './api/api';
import type { CreateSupplementBody } from './api/types';
import type { ClientDetailState } from './types';

const initialState: ClientDetailState = {
  month: currentMonthFirst(),
  capacity: null,
  options: { users: emptyOptionSearch() },
};

export const clientDetailController = createController<ClientDetailState>({
  name: 'clientDetail',
  state: initialState,
  fetchData: async ({ params, queryParams }) => {
    const id = (params as { id?: string } | null)?.id ?? '';
    const month = parseMonth((queryParams as { month?: string } | null)?.month);

    return { month, capacity: await fetchClientCapacity(id, month) };
  },
  asyncActions: {
    queryOptions: optionQueryAction<ClientDetailState>({ users: searchMembers }),
    // Every mutation refetches the one client rather than patching state: the
    // effective total is server-computed from budget + prorated supplements,
    // so a local edit would have to duplicate that math to stay honest.
    upsertBudget: {
      action: async (payload) => {
        const body = payload as {
          clientId: string;
          effectiveMonth: string;
          fixedHours: number;
          variableHours: number;
        };
        await upsertBudget(body);
        return { capacity: await fetchClientCapacity(body.clientId, body.effectiveMonth) };
      },
      handlers: { onSuccess: refreshed },
    },
    createSupplement: {
      action: async (payload) => {
        const body = payload as CreateSupplementBody & { month: string };
        await createSupplement(body);
        return { capacity: await fetchClientCapacity(body.clientId, body.month) };
      },
      handlers: { onSuccess: refreshed },
    },
    removeSupplement: {
      action: async (payload) => {
        const { id, clientId, month } = payload as {
          id: string;
          clientId: string;
          month: string;
        };
        await removeSupplement(id);
        return { capacity: await fetchClientCapacity(clientId, month) };
      },
      handlers: { onSuccess: refreshed },
    },
    upsertAssignment: {
      action: async (payload) => {
        const body = payload as {
          clientId: string;
          userId: string;
          fixedHours: number;
          variableHours: number;
          month: string;
        };
        await upsertAssignment(body);
        return { capacity: await fetchClientCapacity(body.clientId, body.month) };
      },
      handlers: { onSuccess: refreshed },
    },
    removeAssignment: {
      action: async (payload) => {
        const { id, clientId, month } = payload as {
          id: string;
          clientId: string;
          month: string;
        };
        await removeAssignment(id);
        return { capacity: await fetchClientCapacity(clientId, month) };
      },
      handlers: { onSuccess: refreshed },
    },
  },
});

function refreshed(state: ClientDetailState, { action }: { action: unknown }): ClientDetailState {
  return {
    ...state,
    capacity: (action as { payload: { capacity: ClientDetailState['capacity'] } }).payload.capacity,
  };
}
